import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { signIn, signUp, requestPasswordReset, confirmPasswordReset } from '../api'

const { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiArrowRight, FiAlertCircle, FiArrowLeft, FiCheck, FiRefreshCw } = FiIcons

const LoginPage = ({ onAuthSuccess, onAuthError, authError }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('error') // 'error' or 'success'
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', fullName: '', token: '' })

  useEffect(() => {
    // Check for reset token in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setIsResetPassword(true);
      setIsLogin(false);
      setIsForgotPassword(false);
      setFormData(prev => ({ ...prev, token }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setMessage('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setMessageType('error')

    try {
      if (isForgotPassword) {
        await requestPasswordReset(formData.email)
        setMessageType('success')
        setMessage('Password reset link has been sent to your email.')
      } else if (isResetPassword) {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match')
        }
        await confirmPasswordReset(formData.token, formData.password)
        setMessageType('success')
        setMessage('Password reset successful. You can now sign in.')
        setTimeout(() => toggleMode('login'), 3000)
      } else if (isLogin) {
        const data = await signIn(formData.email, formData.password)
        onAuthSuccess(data.user)
        window.dispatchEvent(new Event('auth-state-change'))
      } else {
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters')
        }
        const data = await signUp(formData.email, formData.password, formData.fullName)
        onAuthSuccess(data.user)
        window.dispatchEvent(new Event('auth-state-change'))
      }
    } catch (error) {
      if (onAuthError) onAuthError(error)
      setMessage(error.message || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const toggleMode = (mode) => {
    if (mode === 'login') {
      setIsLogin(true)
      setIsForgotPassword(false)
      setIsResetPassword(false)
    } else if (mode === 'register') {
      setIsLogin(false)
      setIsForgotPassword(false)
      setIsResetPassword(false)
    } else if (mode === 'forgot') {
      setIsLogin(false)
      setIsForgotPassword(true)
      setIsResetPassword(false)
    }
    setMessage('')
    setFormData({ email: '', password: '', confirmPassword: '', fullName: '', token: formData.token })
  }

  const getTitle = () => {
    if (isResetPassword) return 'Reset Your Password'
    if (isForgotPassword) return 'Reset Password'
    if (isLogin) return 'Welcome Back'
    return 'Create Account'
  }

  const getSubtitle = () => {
    if (isResetPassword) return 'Enter your new password below'
    if (isForgotPassword) return 'Enter your email to receive password reset instructions'
    if (isLogin) return 'Sign in to your relationship tracker'
    return 'Join us to start tracking relationships'
  }

  const getIcon = () => {
    if (isResetPassword) return FiLock
    if (isForgotPassword) return FiMail
    return FiUser
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={getIcon()} className="text-2xl text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">{getTitle()}</h1>
          <p className="text-gray-600 mt-2">{getSubtitle()}</p>
        </div>

        {(message || authError) && (
          <div className={`mb-6 p-4 rounded-lg ${messageType === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={messageType === 'success' ? FiCheck : FiAlertCircle} className={messageType === 'success' ? "text-green-600" : "text-red-600"} />
              <p className={messageType === 'success' ? "text-green-700" : "text-red-700"}>{message || authError}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && !isForgotPassword && !isResetPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter your full name" />
              </div>
            </div>
          )}
          {!isResetPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter your email" />
              </div>
            </div>
          )}
          {!isForgotPassword && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{isResetPassword ? 'New Password' : 'Password'}</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required minLength={6} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={isResetPassword ? 'Enter your new password' : 'Enter your password'} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>
              {isResetPassword && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required minLength={6} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Confirm your new password" />
                  </div>
                </div>
              )}
            </>
          )}
          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2">
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <span>{isResetPassword ? 'Update Password' : isLogin ? 'Sign In' : isForgotPassword ? 'Send Reset Instructions' : 'Create Account'}</span>
                <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          {isResetPassword || isForgotPassword ? (
            <button onClick={() => toggleMode('login')} className="flex items-center justify-center space-x-1 text-blue-600 hover:text-blue-800 mx-auto">
              <SafeIcon icon={FiArrowLeft} /> <span>Back to login</span>
            </button>
          ) : (
            <>
              {isLogin && (
                <button onClick={() => toggleMode('forgot')} className="block w-full text-blue-600 hover:text-blue-800 mb-3">
                  Forgot your password?
                </button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default LoginPage